import React, { useState, useEffect, useReducer } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { EMPTY_OBJECT } from './AddTaskList'

function SegmentationAssignment(props) {
    const [sqLDbList, setSqLDbList] = useState([])
    const [databaseId, setDatabaseId] = useState(-1)
    const [dataSources, setDataSources] = useState([])
    const [dataSourceSystemName, setDataSourceSystemName] = useState('')
    const [dimensions, setDimensions] = useState([])
    const [loadedMembers, setLoadedMembers] = useState([])

    // UI State Variables
    const [isLoadingRequest, setIsLoadingRequest] = useState(false)
    const [selectedDimension, setSelectedDimension] = useState('')
    const [changedSegmentation, setChangedSegmentation] = useState([])
    const [, forceUpdate] = useReducer(x => x + 1, 0)

    //#region useEffect
    // trigger on component mount
    useEffect(() => {
        if (sqLDbList.length === 0) {
            let dBList = props.databases.filter(x => x.serverType == 0)
            setSqLDbList(dBList)
        }

        // This is true if we are in edit mode
        if (props.taskData.databaseId !== -1) {
            callForPropDataToLoadExisting()
        } else {
            resetStateData()
        }
    }, [])
    useEffect(() => {
        // This is true if we are in edit mode
        if (props.taskData.databaseId !== -1) {
            if (props.taskData.databaseId !== databaseId) {
                callForPropDataToLoadExisting()
            }
        } else {
            resetStateData()
        }
    }, [props.taskData.databaseId, props.taskData.segmentationAssignments])
    //#endregion

    /**
     * If component has a prop for the [onSave] event, the purpose of this is to fire that event with the latest information
     * @param {int} dbId - Selected database ID
     * @param {string} dsSystemName - Data source system name from the selected database
     */
    function fireOnSaveEvent(dbId, dsSystemName, changedAssignments) {
        if (props.onSave) {
            let copied = JSON.parse(JSON.stringify(EMPTY_OBJECT))
            copied["databaseId"] = dbId
            copied["dataSourceSystemName"] = dsSystemName
            copied["segmentationAssignments"] = changedAssignments
            props.onSave(copied)
        }
    }

    //#region State Set Methods
    function resetStateData() {
        setDatabaseId(-1)
        setDataSources([])
        setDataSourceSystemName('')
        setDimensions([])
        setIsLoadingRequest(false)
        setSelectedDimension('')
        setLoadedMembers([])
    }
    function callForPropDataToLoadExisting() {
        setDatabaseId(props.taskData.databaseId)
        setIsLoadingRequest(true)

        if (props.taskData.segmentationAssignments && props.taskData.segmentationAssignments.length > 0) {
            setChangedSegmentation(props.taskData.segmentationAssignments)
        }

        let getDataSources = props.getDataSources(props.taskData.databaseId)
        getDataSources.then((resp) => { 
            if (resp.status !== 200) {
                throw new Error(resp)
            } else {
                return resp.json()
            }
        }).then((json) => {
            setDataSources(json)
            setDataSourceSystemName(props.taskData.dataSourceSystemName)
            setDimensions([])
            setLoadedMembers([])

            let getDimensions = props.getDimensions(props.taskData.databaseId, props.taskData.dataSourceSystemName)
            getDimensions.then((resp) => { 
                if (resp.status !== 200) {
                    throw new Error(resp)
                } else {
                    return resp.json()
                }
            }).then((json) => {
                let dimensionsFromDataSource = json.filter(x => x.typeOfDimension !== -1)
                
                setIsLoadingRequest(false)
                setDimensions(dimensionsFromDataSource)
                fireOnSaveEvent(props.taskData.databaseId, props.taskData.dataSourceSystemName, [])
            }).catch((ex) => {
                console.error(ex)
                resetStateData()
            })
        }).catch((ex) => {
            console.error(ex)
            resetStateData()
        })
    }
    function loadMoreDimensionMembers(dbId, dsSystemName, selectedDimensionSystemName, alreadyLoaded) {
        setIsLoadingRequest(true)
        let copiedAlreadyLoaded = getSystemIdsOfDimensionAlreadyLoadedMembers(loadedMembers, selectedDimensionSystemName)
        let getDimensionMembersCall = props.getDimensionMembers(dbId, dsSystemName, selectedDimensionSystemName, copiedAlreadyLoaded, '')
        getDimensionMembersCall.then((resp) => { 
            if (resp.status !== 200) {
                throw new Error(resp)
            } else {
                return resp.json()
            }
        }).then((json) => {
            setIsLoadingRequest(false)
            
            // Add already loaded to the state
            let newCopiedAlreadyLoaded = alreadyLoaded
            for (let x = 0; x < json.length; x++) {
                json[x]["dimensionSystemName"] = selectedDimensionSystemName
                newCopiedAlreadyLoaded.push(json[x])
            }
            setLoadedMembers(newCopiedAlreadyLoaded)
        }).catch((ex) => {
            console.error(ex)
            setIsLoadingRequest(false)
        })
    }
    //#endregion

    //#region Handlers Events
    function handleDatabase(event) {
        event.preventDefault()
        setDatabaseId(event.target.value)
        setIsLoadingRequest(true)
        setDataSources([])
        setDataSourceSystemName('')
        setSelectedDimension('')
        setLoadedMembers([])

        let getDataSources = props.getDataSources(event.target.value)
        getDataSources.then((resp) => { 
            if (resp.status !== 200) {
                throw new Error(resp)
            } else {
                return resp.json()
            }
        }).then((json) => {
            setDataSources(json)
            setIsLoadingRequest(false)
            fireOnSaveEvent(event.target.value, '', [])
        }).catch((ex) => {
            console.error(ex)
            setDataSources([])
            setDimensions([])
            setSelectedDimension('')
            setDataSourceSystemName('')
            setLoadedMembers([])
            setIsLoadingRequest(false)
        })
    }
    function handleDataSourceChange(event) {
        event.preventDefault()
        setDataSourceSystemName(event.target.value)
        setDimensions([])
        setLoadedMembers([])
        setIsLoadingRequest(true)

        let getDimensions = props.getDimensions(databaseId, event.target.value)
        getDimensions.then((resp) => { 
            if (resp.status !== 200) {
                throw new Error(resp)
            } else {
                return resp.json()
            }
        }).then((json) => {
            let dimensionsFromDataSource = json.filter(x => x.typeOfDimension !== -1)

            setIsLoadingRequest(false)
            setDimensions(dimensionsFromDataSource)
            fireOnSaveEvent(databaseId, event.target.value, [])
        }).catch((ex) => {
            console.error(ex)
            setDataSources([])
            setDimensions([])
            setLoadedMembers([])
            setSelectedDimension('')
            setDataSourceSystemName('')
            setIsLoadingRequest(false)
        })
    }
    function handleDimensionChange(event) {
        event.preventDefault()
        setSelectedDimension(event.target.value)
        setIsLoadingRequest(true)

        let alreadyLoaded = getSystemIdsOfDimensionAlreadyLoadedMembers(loadedMembers, event.target.value)
        let getDimensionMembersCall = props.getDimensionMembers(databaseId, dataSourceSystemName, event.target.value, alreadyLoaded, '')
        getDimensionMembersCall.then((resp) => { 
            if (resp.status !== 200) {
                throw new Error(resp)
            } else {
                return resp.json()
            }
        }).then((json) => {
            setIsLoadingRequest(false)
            
            // Add already loaded to the state
            let copiedAlreadyLoaded = loadedMembers
            for (let x = 0; x < json.length; x++) {
                json[x]["dimensionSystemName"] = event.target.value
                copiedAlreadyLoaded.push(json[x])
            }
            setLoadedMembers(copiedAlreadyLoaded)
        }).catch((ex) => {
            console.error(ex)
            setIsLoadingRequest(false)
        })
    }
    //#endregion

    //#region Render Methods
    function renderSegmentationAssignmentTable(selectedDimensionSystemName, dimensionData, membersWeAlreadyLoaded, changedSegmentationList, dbId, dsSystemName) {
        let dimensionLookup = dimensionData.filter(x => x.systemName === selectedDimensionSystemName)
        let segmentationHeaders = []
        let memberRows = []

        if (dimensionLookup.length > 0) {
            let dimensionInQuestion = dimensionLookup[0]
            let membersForDimension = membersWeAlreadyLoaded.filter(x => x.dimensionSystemName === selectedDimensionSystemName)
            
            let segmentationForDimension = dimensionInQuestion["segments"]
            
            // For headers area
            for (let segmentItr = 0; segmentItr < segmentationForDimension.length; segmentItr++) {
                segmentationHeaders.push(<TableCell  align="center">{segmentationForDimension[segmentItr]["alias"]}</TableCell>)
            }

            for (let memberItr = 0; memberItr < membersForDimension.length; memberItr++) {
                let segmentationDropdowns = []
                for (let segmentItr = 0; segmentItr < segmentationForDimension.length; segmentItr++) {
                    let segmentMemberDropDowns = []
                    for (let memberItr = 0; memberItr < segmentationForDimension[segmentItr]["memberModels"].length; memberItr++) {
                        segmentMemberDropDowns.push(<MenuItem name={segmentationForDimension[segmentItr]["systemName"]} value={segmentationForDimension[segmentItr]["memberModels"][memberItr]["alias"]}>{segmentationForDimension[segmentItr]["memberModels"][memberItr]["alias"]}</MenuItem>)
                    }

                    segmentationDropdowns.push(<TableCell>
                        <Select
                            id={`select-segment-${segmentationForDimension[segmentItr]["systemName"]}-${membersForDimension[memberItr]["systemName"]}-value`}
                            onChange={(e) => {
                                e.preventDefault()
                                let newObject = { "memberSystemName": membersForDimension[memberItr]["systemName"], "dimensionSystemName": membersForDimension[memberItr]["dimensionSystemName"], "segmentSystemName": segmentationForDimension[segmentItr]["systemName"], "segmentValue": e.target.value }
                                let copied = changedSegmentationList
                                let index = getIndexForMatchingItemOnList(copied, membersForDimension[memberItr]["systemName"], membersForDimension[memberItr]["dimensionSystemName"], segmentationForDimension[segmentItr]["systemName"])
                                if (index === -1) {
                                    copied.push(newObject)
                                } else {
                                    copied[index] = newObject
                                }
                                setChangedSegmentation(copied)
                                fireOnSaveEvent(dbId, dsSystemName, copied)
                                forceUpdate()
                            }}
                            defaultValue={''}
                            value={renderMemberSegmentValue(membersForDimension[memberItr], membersForDimension[memberItr], segmentationForDimension[segmentItr], changedSegmentationList)}
                            fullWidth>
                                <MenuItem name="" value={''}>No Assignment</MenuItem>
                                {segmentMemberDropDowns}
                        </Select>
                    </TableCell>)
                }
                memberRows.push(<TableRow key={`table-row-segmentaion-assignment-${membersForDimension[memberItr]["systemName"]}`}>
                    <TableCell>{membersForDimension[memberItr]["alias"]}</TableCell>
                    {segmentationDropdowns}
                </TableRow>)
            }
        }
        
        return <TableContainer component={Paper}>
            <Table size="small" aria-label="Segmentation Assignment Table">
                <TableHead>
                    <TableRow>
                        <TableCell>Member Name</TableCell>
                        {segmentationHeaders}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {memberRows}
                </TableBody>
            </Table>
        </TableContainer>
    }
    function renderMemberSegmentValue(memberInfo, dimensionMemberInfo, segmentationInfo, changedSegmentationList) {
        let index = getIndexForMatchingItemOnList(changedSegmentationList, memberInfo["systemName"], dimensionMemberInfo["dimensionSystemName"], segmentationInfo["systemName"])
        if (index > -1) {
            return changedSegmentationList[index]["segmentValue"]
        }

        if (dimensionMemberInfo["attributes"][segmentationInfo["systemName"]]) {
            return dimensionMemberInfo["attributes"][segmentationInfo["systemName"]]
        }
        return ''
    }
    //#endregion

    //#region Data Handler Methods
    function getIndexForMatchingItemOnList(changedSegmentationList, memberSystemName, dimensionSystemName, segmentSystemName) {
        for (let segmentItr = 0; segmentItr < changedSegmentationList.length; segmentItr++) {
            let current = changedSegmentationList[segmentItr]
            
            if (current["dimensionSystemName"] === dimensionSystemName && current["segmentSystemName"] === segmentSystemName && current["memberSystemName"] === memberSystemName) {
                return segmentItr
            }
        }

        return -1
    }
    function getSystemIdsOfDimensionAlreadyLoadedMembers(copiedAlreadyLoaded, dimensionSystemName) {
        let rtnVal = []
        let dimensionCopied = copiedAlreadyLoaded.filter(x => x.dimensionSystemName === dimensionSystemName)

        for (let copiedItr = 0; copiedItr < dimensionCopied.length; copiedItr++) {
            let current = dimensionCopied[copiedItr]
            rtnVal.push(parseInt(current["systemName"]))
        }

        return rtnVal
    }
    //#endregion

    return (<Paper elevation={3} className="dbFunctionsPaper">
        <h3>Add Segments</h3>
        <FormControl className="field" fullWidth>
            <InputLabel id="select-databse-label">Select Database</InputLabel>
            <Select
                labelId="select-databse-label"
                id="select-databse"
                label="Select Database"
                onChange={handleDatabase}
                defaultValue={sqLDbList.length > 0 ? sqLDbList[0].id : -1}
                value={databaseId}
                required
                fullWidth
                disabled={isLoadingRequest}>
                    <MenuItem value={-1}>Select Database</MenuItem>
                    { sqLDbList.map((x) => x && <MenuItem key={x.id} value={x.id}>{x.dBname}</MenuItem>)}
            </Select>
        </FormControl>
        <FormControl className="field" fullWidth>
            <InputLabel id="select-data-source-label">Select Data Source</InputLabel>
            <Select
                labelId="select-data-source-label"
                id="select-data-source"
                label="Select Data Source"
                onChange={handleDataSourceChange}
                value={dataSourceSystemName}
                defaultValue={''}
                required
                fullWidth
                disabled={isLoadingRequest}>
                    <MenuItem value={''}>Select Data Source</MenuItem>
                    { dataSources.map((x) => x && <MenuItem key={x.key} value={x.key}>{x.value}</MenuItem>)}
            </Select>
        </FormControl>
        <FormControl className="field" fullWidth>
            <InputLabel id="select-dimension-label">Select Dimension</InputLabel>
            <Select
                labelId="select-dimension-label"
                id="select-dimension"
                label="Select Dimension"
                onChange={handleDimensionChange}
                value={selectedDimension}
                defaultValue={''}
                required
                fullWidth
                disabled={isLoadingRequest}>
                    <MenuItem value={''}>Select Dimension</MenuItem>
                    { dimensions.map((x) => x && <MenuItem key={x.systemName} value={x.systemName}>{x.alias}</MenuItem>)}
            </Select>
        </FormControl>
        <Box sx={{ width: '100%', bgcolor: 'background.paper', maxHeight: '250px', overflowY: 'scroll', marginTop: '15px' }}>
            {renderSegmentationAssignmentTable(selectedDimension, dimensions, loadedMembers, changedSegmentation, databaseId, dataSourceSystemName)}
        </Box>
        <Button variant="contained" fullWidth disabled={isLoadingRequest || dataSourceSystemName.trim().length === 0 || selectedDimension.trim().length === 0} onClick={(e) => {
            e.preventDefault()
            loadMoreDimensionMembers(databaseId, dataSourceSystemName, selectedDimension, loadedMembers)
        }}>Load More Members</Button>
    </Paper>)
}

export default SegmentationAssignment